import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Pen, Upload, Trash2, Check } from 'lucide-react';
import ImagePicker from '../cms/ImagePicker';

const SignaturePad = ({ value, onChange, instructorId, label = 'Signature' }) => {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState(value ? 'preview' : 'choose'); // choose | draw | upload | preview
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#1D1D1B';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setHasStrokes(false);
  }, []);

  useEffect(() => {
    if (mode === 'draw') {
      setTimeout(initCanvas, 50);
    }
  }, [mode, initCanvas]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasStrokes(true);
  };

  const stopDraw = () => setIsDrawing(false);

  const clearCanvas = () => { initCanvas(); };

  const saveDrawing = async () => {
    if (!canvasRef.current || !hasStrokes) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    if (instructorId) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/academy/instructors/${instructorId}/signature`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ data_url: dataUrl }),
        });
        const data = await res.json();
        if (data.signature_url) {
          onChange(data.signature_url);
          setMode('preview');
        }
      } catch { /* fallback: pass data URL */ onChange(dataUrl); setMode('preview'); }
    } else {
      onChange(dataUrl);
      setMode('preview');
    }
  };

  const handleRemove = () => { onChange(''); setMode('choose'); };

  if (mode === 'preview' && value) {
    const imgSrc = value.startsWith('data:') ? value : `${API_URL}${value}`;
    return (
      <div className="space-y-2" data-testid="signature-preview">
        <Label>{label}</Label>
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex-1 h-16 flex items-center justify-center bg-white rounded border border-dashed border-slate-200">
              <img src={imgSrc} alt="Signature" className="max-h-14 max-w-full object-contain" />
            </div>
            <Button type="button" variant="ghost" size="sm" className="text-red-500 shrink-0" onClick={handleRemove} data-testid="signature-remove-btn">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'draw') {
    return (
      <div className="space-y-2" data-testid="signature-draw-mode">
        <Label>{label} — Dessiner</Label>
        <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
          <canvas
            ref={canvasRef}
            className="w-full cursor-crosshair touch-none"
            style={{ height: '120px' }}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
            data-testid="signature-canvas"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setMode('choose')} className="flex-1">Annuler</Button>
          <Button type="button" variant="outline" size="sm" onClick={clearCanvas} className="flex-1">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Effacer
          </Button>
          <Button type="button" size="sm" onClick={saveDrawing} disabled={!hasStrokes} className="flex-1" data-testid="signature-save-btn">
            <Check className="w-3.5 h-3.5 mr-1" /> Confirmer
          </Button>
        </div>
      </div>
    );
  }

  if (mode === 'upload') {
    return (
      <div className="space-y-2" data-testid="signature-upload-mode">
        <Label>{label} — Telecharger</Label>
        <ImagePicker value={value} onChange={(url) => { onChange(url); if (url) setMode('preview'); }} folder="signatures" label="" />
        <Button type="button" variant="outline" size="sm" onClick={() => setMode('choose')} className="w-full">Retour</Button>
      </div>
    );
  }

  // mode === 'choose'
  return (
    <div className="space-y-2" data-testid="signature-choose-mode">
      <Label>{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" onClick={() => setMode('draw')} className="h-16 flex-col gap-1" data-testid="signature-draw-btn">
          <Pen className="w-5 h-5 text-[#F9B438]" />
          <span className="text-xs">Dessiner</span>
        </Button>
        <Button type="button" variant="outline" onClick={() => setMode('upload')} className="h-16 flex-col gap-1" data-testid="signature-upload-btn">
          <Upload className="w-5 h-5 text-[#F9B438]" />
          <span className="text-xs">Telecharger</span>
        </Button>
      </div>
    </div>
  );
};

export default SignaturePad;
